import { INVENTORY_MODULE } from "@/api_framework/api_config";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

export interface IClinicToDoctorListBody {
  clinic_ids?: string[];
  doctor_ids?: string[];
  statuses?: string[];
  start_ts?: number;
  end_ts?: number;
  page?: number;
  page_size?: number;
  itr_number?: string;
  direction?: string;
  include_mapped_warehouse?: boolean;
}

export interface IClinicToDoctorLotDetails {
  lot_number: string;
  expiry_date: string;
  mrp: number;
  manufacturer_name: string;
  grn_id: string;
  sub_grn_id: string;
}

export interface IClinicToDoctorItem {
  unique_row_id: string;
  vetic_variant_id: string;
  name: string;
  qty: number;
  lot_details: IClinicToDoctorLotDetails;
  source_type?: string;
}

export interface IClinicToDoctorRequestItem {
  request_id: string;
  request_number: string;
  clinic_id: string;
  doctor_id: string;
  direction: string;
  clinic_name: string;
  doctor_name: string;
  doctor_email: string;
  items: IClinicToDoctorItem[];
  status: string;
  action_buttons: string[];
  rota_date: string;
  transfer_grn_id: string;
  transfer_sub_grn_id: string;
  created_by: string;
  created_at: number;
  updated_by: string;
  updated_at: number;
  cancellation_reason: string | null;
}

interface IClinicToDoctorListResponseData {
  total: number;
  page: number;
  page_size: number;
  items: IClinicToDoctorRequestItem[];
}

interface IClinicToDoctorListResponse {
  status: boolean;
  message: string;
  data: IClinicToDoctorListResponseData;
}

export const useClinicToDoctorRequestListAPI = () => {
  const [items, setItems] = useState<IClinicToDoctorRequestItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();

  const execute = useCallback(async (body: IClinicToDoctorListBody) => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      await axios
        .post(INVENTORY_MODULE.CLINIC_TO_DOCTOR_REQUEST_LIST.baseURL, body, {
          headers: {
            Authorization: `${accessToken}`,
            "Content-Type": "application/json",
          },
        })
        .then((res: AxiosResponse<IClinicToDoctorListResponse>) => {
          if (res.data.status === true) {
            setItems(res.data.data?.items ?? []);
            setTotal(res.data.data?.total ?? 0);
          } else {
            setItems([]);
            setTotal(0);
          }
        })
        .catch((e: AxiosError) => {
          captureError(e);
          setItems([]);
          setTotal(0);
        });
    } catch (e: any) {
      toast.error("Server Error: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, items, total, loading };
};
